import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { 
  Store, Wallet, CreditCard, Calendar, FileText,
  DollarSign, Check, Clock, AlertCircle, Copy
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value, moeda = "R$") => {
  if (moeda === "€") {
    return new Intl.NumberFormat("pt-PT", {
      style: "currency",
      currency: "EUR"
    }).format(value);
  }
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(value);
};

export default function RevendaPublicoPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/revendas/publico/${token}`);
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Link inválido ou expirado");
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchData();
    } else {
      setError("Token não fornecido");
      setLoading(false);
    }
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-400">Carregando informações...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-slate-800/50 border-red-500/50">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Erro</h2>
            <p className="text-slate-400">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const lancamentosConfirmados = data.lancamentos.filter(l => l.confirmado);
  const lancamentosPendentes = data.lancamentos.filter(l => !l.confirmado);

  const copyPix = (pix, label) => {
    navigator.clipboard.writeText(pix).then(() => {
      toast.success(`${label} copiado!`);
    }).catch(() => {
      toast.error("Erro ao copiar");
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 md:p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* PIX Section - No topo */}
        {(data.pix_1 || data.pix_2) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.pix_1 && (
              <Card className="bg-emerald-500/10 border-emerald-500/30 cursor-pointer hover:bg-emerald-500/20 transition-all"
                    onClick={() => copyPix(data.pix_1, "PIX 1")}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-emerald-500" />
                      </div>
                      <div>
                        <p className="text-xs text-emerald-400 font-medium">PIX 1 - Clique para copiar</p>
                        <p className="text-white font-mono text-sm truncate max-w-[200px]">{data.pix_1}</p>
                        {(data.pix_1_banco || data.pix_1_nome) && (
                          <p className="text-xs text-slate-400 mt-1">
                            {data.pix_1_banco && <span className="text-emerald-400">{data.pix_1_banco}</span>}
                            {data.pix_1_banco && data.pix_1_nome && " • "}
                            {data.pix_1_nome && <span>{data.pix_1_nome}</span>}
                          </p>
                        )}
                      </div>
                    </div>
                    <Copy className="w-5 h-5 text-emerald-500" />
                  </div>
                </CardContent>
              </Card>
            )}
            {data.pix_2 && (
              <Card className="bg-emerald-500/10 border-emerald-500/30 cursor-pointer hover:bg-emerald-500/20 transition-all"
                    onClick={() => copyPix(data.pix_2, "PIX 2")}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-emerald-500" />
                      </div>
                      <div>
                        <p className="text-xs text-emerald-400 font-medium">PIX 2 - Clique para copiar</p>
                        <p className="text-white font-mono text-sm truncate max-w-[200px]">{data.pix_2}</p>
                        {(data.pix_2_banco || data.pix_2_nome) && (
                          <p className="text-xs text-slate-400 mt-1">
                            {data.pix_2_banco && <span className="text-emerald-400">{data.pix_2_banco}</span>}
                            {data.pix_2_banco && data.pix_2_nome && " • "}
                            {data.pix_2_nome && <span>{data.pix_2_nome}</span>}
                          </p>
                        )}
                      </div>
                    </div>
                    <Copy className="w-5 h-5 text-emerald-500" />
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Store className="w-10 h-10 text-emerald-500" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
            {data.nome}
          </h1>
          <p className="text-slate-400">Consulta de lançamentos e saldo</p>
        </div>

        {/* Resumo Financeiro */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-amber-500/10 border-amber-500/30">
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Wallet className="w-5 h-5 text-amber-500" />
                <span className="text-sm font-bold text-amber-500">SOMA TOTAL</span>
              </div>
              <p className="text-2xl font-bold text-amber-500">
                {formatCurrency(data.soma_total)}
              </p>
              <p className="text-xs text-slate-400 mt-1">Lançamentos pendentes</p>
            </CardContent>
          </Card>

          <Card className="bg-emerald-500/10 border-emerald-500/30">
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <CreditCard className="w-5 h-5 text-emerald-500" />
                <span className="text-sm font-bold text-emerald-500">SALDO</span>
              </div>
              <p className="text-2xl font-bold text-emerald-500">
                {formatCurrency(data.saldo)}
              </p>
              <p className="text-xs text-slate-400 mt-1">Lançamentos confirmados</p>
            </CardContent>
          </Card>

          <Card className={`${data.valor_a_pagar > 0 ? "bg-red-500/10 border-red-500/30" : "bg-blue-500/10 border-blue-500/30"}`}>
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <DollarSign className={`w-5 h-5 ${data.valor_a_pagar > 0 ? "text-red-500" : "text-blue-500"}`} />
                <span className={`text-sm font-bold ${data.valor_a_pagar > 0 ? "text-red-500" : "text-blue-500"}`}>A PAGAR</span>
              </div>
              <p className={`text-2xl font-bold ${data.valor_a_pagar > 0 ? "text-red-500" : "text-blue-500"}`}>
                {formatCurrency(data.valor_a_pagar)}
              </p>
              <p className="text-xs text-slate-400 mt-1">Soma Total - Saldo</p>
            </CardContent>
          </Card>
        </div>

        {/* Lançamentos Pendentes */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              Lançamentos Pendentes ({lancamentosPendentes.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {lancamentosPendentes.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Check className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Nenhum lançamento pendente</p>
              </div>
            ) : (
              <div className="space-y-3">
                {lancamentosPendentes.map((lancamento) => (
                  <div
                    key={lancamento.id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-amber-500/10 rounded-lg border border-amber-500/30 gap-3"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-amber-400">
                          {formatCurrency(lancamento.valor, lancamento.moeda)}
                        </span>
                        <Badge variant="outline" className="text-amber-500 border-amber-500">
                          Pendente
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-slate-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {lancamento.data}
                        </span>
                        {lancamento.servico && (
                          <span>{lancamento.servico}</span>
                        )}
                        {lancamento.mec_creditos && (
                          <span>MEC: {lancamento.mec_creditos}</span>
                        )}
                      </div>
                      {lancamento.descricao && (
                        <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                          <FileText className="w-3 h-3" />
                          {lancamento.descricao}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Lançamentos Confirmados */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Check className="w-5 h-5 text-emerald-500" />
              Lançamentos Confirmados ({lancamentosConfirmados.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {lancamentosConfirmados.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Nenhum lançamento confirmado ainda</p>
              </div>
            ) : (
              <div className="space-y-3">
                {lancamentosConfirmados.map((lancamento) => (
                  <div
                    key={lancamento.id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/30 gap-3"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-emerald-400">
                          {formatCurrency(lancamento.valor, lancamento.moeda)}
                        </span>
                        <Badge className="bg-emerald-500">
                          Confirmado
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-slate-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {lancamento.data}
                        </span>
                        {lancamento.servico && (
                          <span>{lancamento.servico}</span>
                        )}
                        {lancamento.mec_creditos && (
                          <span>MEC: {lancamento.mec_creditos}</span>
                        )}
                      </div>
                      {lancamento.descricao && (
                        <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                          <FileText className="w-3 h-3" />
                          {lancamento.descricao}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-slate-500 text-sm pt-4">
          <p>Esta é uma consulta de visualização apenas.</p>
          <p>Para dúvidas, entre em contato com o responsável.</p>
        </div>
      </div>
    </div>
  );
}
